import { useMemo, useState, useRef, MutableRefObject } from 'react';
import { css, cx } from '@linaria/atomic';
import { Reorder, useDragControls, motion, VariantLabels } from 'motion/react';
import { onlyText } from 'react-children-utilities';
import type { ColumnInstance, IdType, TableInstance } from 'react-table';
import { theme } from '@frontend/theme';
import { ColumnsIcon } from '../../../icon/icons/columns';
import { HamburgerMenuIcon } from '../../../icon/icons/hamburger-menu';
import { CheckboxField } from '../../forms/fields/checkbox/checkbox-field.component';
import { useFormField } from '../../forms/hooks/use-form-field/use-form-field';
import { IconButton } from '../../icon-button/icon-button.component';
import { PopoverDialog } from '../../popover/dialog/popover-dialog.component';
import { usePopoverDialog } from '../../popover/dialog/use-popover-dialog';
import { Text } from '../../text/text.component';
import { TextLink } from '../../text-link/text-link.component';
import { TableCommonStyles } from '../common-styles';
import { TableData } from '../table-data-type';
import { getColumnIdsWithStickyOrder, isReservedCellId, persistUserPreference } from '../utils/table-utils';

const filterColPositionStyle = css`
  position: relative;
`;

const filterColMarginLeftStyle = css`
  margin-left: ${theme.spacing(1)};
`;

const filterColIconBtnAfterVisibleStyle = css`
  &::after {
    opacity: 1;
  }
`;

const filterColIconBtnAfterHiddenStyle = css`
  &::after {
    opacity: 0;
  }
`;

const columnFiltersMotionArticleStyle = css`
  width: 100%;
`;

const columnFiltersReorderGroupStyle = css`
  position: relative;
  padding: 0;
`;

const columnFilterItemHamburgerStyle = css`
  margin-right: ${theme.spacing(1.5)};
`;

const columnFilterItemHamburgerGrabStyle = css`
  cursor: grab;
`;

const columnFilterItemHamburgerGrabbingStyle = css`
  cursor: grabbing;
`;

const columnFilterItemCheckboxStyle = css`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const filterColumnsDialogStyle = css`
  width: 330px;
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing(1, 0)};
`;

const columnFilterItemStyle = css`
  position: relative;
  min-height: ${theme.spacing(5)};
  list-style: none;
  display: flex;
  align-items: center;
  padding: ${theme.spacing(1, 2)};
  background: ${theme.colors.white};
`;

type FilterColumnsContainerProps<T extends TableData> = {
  tableInstance: TableInstance<T>;
  isFilterEnabled?: boolean;
  onChange?: (changeEvent: { hiddenColumns?: string[]; columnOrder?: string[] }) => void;
};

export const FilterColumnsContainer = <T extends TableData>({
  tableInstance,
  isFilterEnabled,
  onChange,
}: FilterColumnsContainerProps<T>) => {
  const {
    state: { hiddenColumns },
    filterColumnsConfig,
    tableTrackingIds,
  } = tableInstance;
  const notificationBadgeStyles = TableCommonStyles.filterNotificationBadgeStyles;
  const { getTriggerProps, getDialogProps } = usePopoverDialog<HTMLButtonElement | HTMLAnchorElement>({
    placement: 'bottom-end',
    initialOffset: { x: -290, y: 40 },
  });

  return (
    <section className={cx(filterColPositionStyle, isFilterEnabled && filterColMarginLeftStyle)}>
      <IconButton
        showLabelOnHover
        disabled={tableInstance.isLoading}
        color='light'
        trackingId={tableTrackingIds.filterColumns}
        data-test-id={tableTrackingIds.filterColumns}
        {...getTriggerProps()}
        className={cx(
          notificationBadgeStyles,
          hiddenColumns?.length ? filterColIconBtnAfterVisibleStyle : filterColIconBtnAfterHiddenStyle
        )}
        label={filterColumnsConfig?.label ?? 'Filter Columns'}
      >
        <ColumnsIcon />
      </IconButton>
      <PopoverDialog {...getDialogProps()} className={filterColumnsDialogStyle}>
        <ColumnFilters tableInstance={tableInstance} onChange={onChange} />
      </PopoverDialog>
    </section>
  );
};

type ColumnFilters<T extends TableData> = {
  tableInstance: TableInstance<T>;
  onChange?: (changeEvent: { hiddenColumns?: string[]; columnOrder?: string[] }) => void;
};

export const ColumnFilters = <T extends TableData>({ tableInstance, onChange }: ColumnFilters<T>) => {
  const filterColumnStyle = TableCommonStyles.filterColumnStyles;

  const {
    allColumns,
    toggleHideAllColumns,
    visibleColumns,
    state: { hiddenColumns },
    uniqueFilterColumnsId,
    uniqueOrderColumnsId,
    filterColumnsConfig,
    setColumnOrder,
  } = tableInstance;

  const nonActionColumns = useMemo(() => {
    return allColumns.filter((column) => !isReservedCellId(column.id)).length;
  }, [allColumns.length]);

  const nonActionVisibleColumns = useMemo(() => {
    return visibleColumns.filter((column) => !isReservedCellId(column.id)).length;
  }, [visibleColumns.length]);

  const columnsIds = allColumns.map((column) => column.id);

  const [columnList, setColumnList] = useState(columnsIds);
  const [isDragging, setIsDragging] = useState(false);
  const constraintRef = useRef<HTMLUListElement | null>(null);

  return (
    <motion.article
      initial={false}
      layout='position'
      transition={{
        layout: {
          duration: 0,
        },
      }}
      layoutScroll
      className={columnFiltersMotionArticleStyle}
    >
      <section className={filterColumnStyle}>
        <Text weight='semibold' color='light'>
          {filterColumnsConfig?.label ?? 'Filter Columns'}
        </Text>
        <TextLink
          disabled={nonActionVisibleColumns === nonActionColumns}
          weight='semibold'
          onClick={() => {
            toggleHideAllColumns(false);
            window.localStorage.removeItem(uniqueFilterColumnsId ?? '');
          }}
        >
          {filterColumnsConfig?.showAllLabel ?? 'Show All'}
        </TextLink>
      </section>
      <Reorder.Group
        transition={{
          layout: {
            duration: 0,
          },
        }}
        initial={false}
        layout='position'
        className={columnFiltersReorderGroupStyle}
        axis='y'
        ref={constraintRef}
        values={columnList}
        onReorder={setColumnList}
      >
        {columnList.map((listColumnId) => (
          <ColumnFilterItem
            allColumns={allColumns}
            columnList={columnList}
            hiddenColumns={hiddenColumns}
            listColumnId={listColumnId}
            setColumnOrder={setColumnOrder}
            uniqueFilterColumnsId={uniqueFilterColumnsId}
            uniqueOrderColumnsId={uniqueOrderColumnsId}
            constraintRef={constraintRef}
            setIsDragging={setIsDragging}
            isDragging={isDragging}
            key={listColumnId}
            onChange={onChange}
          />
        ))}
      </Reorder.Group>
    </motion.article>
  );
};

type ColumnFilterItem<T extends TableData> = {
  allColumns: ColumnInstance<T>[];
  columnList: IdType<T>[];
  hiddenColumns: IdType<T>[] | undefined;
  listColumnId: IdType<T>;
  setColumnOrder: TableInstance<T>['setColumnOrder'];
  uniqueFilterColumnsId: string | undefined;
  uniqueOrderColumnsId: string | undefined;
  constraintRef: MutableRefObject<HTMLUListElement | null>;
  setIsDragging: (isDragging: boolean) => void;
  isDragging: boolean;
  onChange?: (changeEvent: { hiddenColumns?: string[]; columnOrder?: string[] }) => void;
};

export const ColumnFilterItem = <T extends TableData>({
  listColumnId,
  setColumnOrder,
  allColumns,
  uniqueOrderColumnsId,
  uniqueFilterColumnsId,
  columnList,
  hiddenColumns,
  constraintRef,
  setIsDragging,
  isDragging,
  onChange,
}: ColumnFilterItem<T>) => {
  const reorderColumnWhileTapStyles = TableCommonStyles.reorderColumnWhileTapStyles;

  const { Header, headerLabel, disableColumnFilter, toggleHidden, id, isVisible } = allColumns.find(
    (column) => column.id === listColumnId
  ) as ColumnInstance<T>;
  const dragControls = useDragControls();
  const fieldProps = useFormField({ type: 'checkbox', value: isVisible }, [isVisible]);

  if ((typeof Header === 'function' && !headerLabel) || isReservedCellId(id) || disableColumnFilter) return null;

  const onDragEnd = () => {
    setColumnOrder(getColumnIdsWithStickyOrder(columnList, allColumns));
    onChange?.({ hiddenColumns: hiddenColumns, columnOrder: columnList });

    persistUserPreference(uniqueOrderColumnsId, columnList);
    setIsDragging(false);
  };

  return (
    <Reorder.Item
      initial={false}
      key={listColumnId}
      id={listColumnId}
      value={listColumnId}
      dragListener={false}
      dragElastic={0.05}
      as={'ul'}
      dragConstraints={constraintRef}
      dragControls={dragControls}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={onDragEnd}
      whileTap={reorderColumnWhileTapStyles as unknown as VariantLabels}
      className={columnFilterItemStyle}
    >
      <HamburgerMenuIcon
        size={20}
        color='light'
        onPointerDown={(event) => dragControls.start(event)}
        className={cx(
          columnFilterItemHamburgerStyle,
          isDragging ? columnFilterItemHamburgerGrabbingStyle : columnFilterItemHamburgerGrabStyle
        )}
      />

      <CheckboxField
        {...fieldProps}
        name={onlyText(headerLabel) ?? onlyText(Header as React.ReactNode)}
        label={headerLabel ?? (Header as React.ReactNode)}
        labelPlacement='left'
        onChange={() => {
          if (!hiddenColumns || !uniqueFilterColumnsId) return;

          toggleHidden(fieldProps.value);

          const hiddenColumnsCurrent = fieldProps.value
            ? [...hiddenColumns, id]
            : hiddenColumns.filter((hiddenColumn) => hiddenColumn !== id);

          if (!hiddenColumnsCurrent.length) {
            window.localStorage.removeItem(uniqueFilterColumnsId);
            return;
          }

          persistUserPreference(uniqueFilterColumnsId, hiddenColumnsCurrent);
          onChange?.({ hiddenColumns: hiddenColumnsCurrent, columnOrder: columnList });
        }}
        className={columnFilterItemCheckboxStyle}
      />
    </Reorder.Item>
  );
};
